import React, { useState, useEffect, useCallback } from 'react';
import {
  ActionIcon,
  Popover,
  Badge,
  Stack,
  Text,
  Group,
  Divider,
  Button,
  ScrollArea,
  Paper,
  rem,
  Box,
  Tooltip
} from '@mantine/core';
import { IconBell, IconCheck, IconMail, IconEye } from '@tabler/icons-react';
import { apiService } from '../services/apiService';
import { useTranslation } from 'react-i18next';
import { isWeb } from '../utils/environment';

interface Notification {
  id: number;
  notification_id: number;
  title: string;
  body: string;
  created_at: string;
  sent_at: string;
  read_at: string | null;
  email_status: string | null;
  email_sent_at: string | null;
}

export const NotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMainVisible, setIsMainVisible] = useState(isWeb());

  const fetchNotifications = useCallback(async () => {
    try {
            const response = await apiService.getNotifications();
      setNotifications(response.notifications || []);
      setUnreadCount(response.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
          }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiService.getUnreadNotificationCount();
      setUnreadCount(response.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  useEffect(() => {
    if (isWeb()) {
      setIsMainVisible(true);
      return;
    }

    let disposed = false;

    import('@tauri-apps/api/window')
      .then(({ getCurrentWindow }) => getCurrentWindow().isVisible())
      .then((visible) => {
        if (!disposed) {
          setIsMainVisible(visible);
        }
      })
      .catch(() => undefined);

    let unlistenShown: (() => void) | undefined;
    let unlistenHidden: (() => void) | undefined;

    import('@tauri-apps/api/event')
      .then(({ listen }) => {
        listen('main-window-shown', () => setIsMainVisible(true)).then((unlisten) => {
          unlistenShown = unlisten;
        });
        listen('main-window-hidden', () => {
          setIsMainVisible(false);
          setOpened(false);
          setNotifications([]);
        }).then((unlisten) => {
          unlistenHidden = unlisten;
        });
      })
      .catch(() => undefined);

    return () => {
      disposed = true;
      if (unlistenShown) unlistenShown();
      if (unlistenHidden) unlistenHidden();
    };
  }, []);

  useEffect(() => {
    if (!isMainVisible) {
      return;
    }

    fetchUnreadCount();
    // Poll for unread count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, isMainVisible]);

  useEffect(() => {
    if (opened && isMainVisible) {
      fetchNotifications();
    }
  }, [opened, fetchNotifications, isMainVisible]);

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await apiService.markNotificationAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.time.justNow', 'Just now');
    if (diffMins < 60) return t('notifications.time.minutesAgo', '{{count}} min ago', { count: diffMins });
    if (diffHours < 24) return t('notifications.time.hoursAgo', '{{count}} hr ago', { count: diffHours });
    if (diffDays < 7) return t('notifications.time.daysAgo', '{{count}} days ago', { count: diffDays });
    return date.toLocaleDateString();
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      withArrow
      shadow="md"
      withinPortal
    >
      <Popover.Target>
        <Box pos="relative">
          <ActionIcon
            variant="subtle"
            size="lg"
            onClick={() => setOpened(!opened)}
            aria-label={t('notifications.title', 'Notifications')}
          >
            <IconBell style={{ width: rem(20), height: rem(20) }} />
          </ActionIcon>
          {unreadCount > 0 && (
            <Badge
              color="red"
              size="xs"
              circle
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                minWidth: rem(18),
                height: rem(18),
                padding: 0,
                fontSize: rem(10),
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Box>
      </Popover.Target>

      <Popover.Dropdown p={0} style={{ width: rem(360) }}>
        <Paper p="sm" withBorder={false}>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>{t('notifications.title', 'Notifications')}</Text>
            {unreadCount > 0 && (
              <Button
                variant="subtle"
                size="compact-xs"
                leftSection={<IconCheck size={14} />}
                onClick={handleMarkAllAsRead}
              >
                {t('notifications.markAllRead', 'Mark all read')}
              </Button>
            )}
          </Group>
          <Divider />
          <ScrollArea.Autosize mah={rem(400)}>
            {notifications.length > 0 ? (
              <Stack gap="xs" mt="xs">
                {notifications.map((notification) => (
                  <Paper
                    key={notification.id}
                    p="sm"
                    withBorder
                    style={{
                      borderLeft: notification.read_at ? undefined : `3px solid var(--mantine-color-blue-6)`,
                    }}
                  >
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Box style={{ flex: 1 }}>
                        <Text fw={500} size="sm">
                          {notification.title}
                        </Text>
                        <Text size="xs" c="dimmed" mt={4}>
                          {notification.body}
                        </Text>
                        <Group gap="xs" mt={8}>
                          <Text size="xs" c="dimmed">
                            {formatDate(notification.sent_at || notification.created_at)}
                          </Text>
                          {notification.email_status === 'sent' && (
                            <Tooltip label={t('notifications.sentViaEmail', 'Also sent via email')}>
                              <IconMail size={14}                               color='dimmed' />
                            </Tooltip>
                          )}
                        </Group>
                      </Box>
                      {!notification.read_at && (
                        <Tooltip label={t('notifications.markAsRead', 'Mark as read')}>
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            size="sm"
                            onClick={() => handleMarkAsRead(notification.id)}
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Box py="xl" ta="center">
                <Text c="dimmed" size="sm">
                  {t('notifications.empty', 'No notifications yet')}
                </Text>
              </Box>
            )}
          </ScrollArea.Autosize>
        </Paper>
      </Popover.Dropdown>
    </Popover>
  );
};
